'use client';

/**
 * Lightweight theme hook (no next-themes dependency). The actual `.dark` class
 * is first applied by the inline no-flash script in app/layout.tsx before paint;
 * this hook hydrates from that, persists changes, and reacts to OS changes while
 * in "system" mode. Default is "light" (opt-in) until dark-mode coverage is
 * complete across all tool/component surfaces — see the dark-mode migration.
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'theme';

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolve a theme choice to the concrete dark/light state and apply it to <html>. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  const el = document.documentElement;
  el.classList.toggle('dark', dark);
  el.style.colorScheme = dark ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* localStorage unavailable */
  }
  return 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>('light');

  // Hydrate from storage on mount. The no-flash script already set the class,
  // so this only syncs React state — no visual flash.
  useEffect(() => {
    setThemeState(readStoredTheme());
  }, []);

  // While in "system" mode, follow OS preference changes live.
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyTheme(next);
  }, []);

  return { theme, setTheme };
}
